import React, { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "../../firebase/firebase";
import TextEditor from "../TextEditor";
import { useNavigate } from "react-router-dom";

function Collection4() {
  const [data, setData] = useState([]);
  const [heading, setHeading] = useState();
  const [headingChange, setHeadingChange] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [imgFile, setImgFile] = useState(null);
  const [existingImgUrl, setExistingImgUrl] = useState(null);
  const [productTitle, setProductTitle] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [oldPrice, setOldPrice] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [isInStock, setIsInStock] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [brand, setBrand] = useState("fesishop");

  const [colorVariants, setColorVariants] = useState([
    { file: null, color: "" },
  ]);

  const collections = [
    { name: "Men Shirts", route: "menShirts" },
    { name: "Women Shirts", route: "womenShirts" },
    { name: "Kids Shirts", route: "kidsShirts" },
  ];

  const db = getFirestore();
  const storage = getStorage();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "collection_4"),
      (querySnapshot) => {
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });

          if (doc.id === "productFilter") {
            setSortOrder(doc.data().type || "newest");
          }

          if (doc.id === "heading") {
            setHeading(doc.data().heading || "Collection 4");
          }
        });
        setData(data);
      }
    );

    return () => unsubscribe();
  }, [db]);

  const filteredData = data
    .filter((item) => item.id !== "heading")
    .filter((item) => item.id !== "productFilter")
    .filter((item) =>
      item.productTitle?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const priceA = parseFloat(a.productPrice);
      const priceB = parseFloat(b.productPrice);

      if (sortOrder === "lowToHigh") return priceA - priceB;
      if (sortOrder === "highToLow") return priceB - priceA;
      if (sortOrder === "newest") return b.createdAt?.seconds - a.createdAt?.seconds;
      if (sortOrder === "oldest") return a.createdAt?.seconds - b.createdAt?.seconds;
      return 0;
    });

  const handleChangeHeading = () => {
    setHeadingChange(!headingChange);
  };

  const handleKeyPress = async (e) => {
    if (e.key === "Enter") {
      setHeadingChange(false);

      const docRef = doc(db, "collection_4", "heading");
      try {
        await updateDoc(docRef, {
          heading: heading,
        });
        console.log("Heading updated successfully!");
      } catch (error) {
        console.error("Error updating heading: ", error);
      }
    }
  };

  const handleFileChange = (e) => {
    setImgFile(e.target.files[0]);
  };

  const handleColorChange = (index, key, value) => {
    const updated = [...colorVariants];
    updated[index][key] = value;
    setColorVariants(updated);
  };

  const addColorVariant = () => {
    setColorVariants([...colorVariants, { file: null, color: "" }]);
  };

  const removeColorVariant = (index) => {
    const updated = [...colorVariants];
    updated.splice(index, 1);
    setColorVariants(updated);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    let imgUrl = existingImgUrl;
    let productId = editId;

    try {
      if (!isEdit) {
        const docRef = await addDoc(collection(db, "collection_4"), {
          productTitle,
          productDescription,
          oldPrice: oldPrice || "",
          productPrice,
          productImage: "",
          inStock: isInStock,
          productColors: [],
          brand,
          createdAt: serverTimestamp(),
        });
        productId = docRef.id;
      }

      if (imgFile) {
        const heroRef = ref(
          storage,
          `collection_4_Images/${productId}/${imgFile.name}`
        );
        await uploadBytes(heroRef, imgFile);
        imgUrl = await getDownloadURL(heroRef);
      }

      const productColors = await Promise.all(
        colorVariants.map(async ({ file, image, color }) => {
          if (file && color) {
            const colorRef = ref(
              storage,
              `collection_4_Images/${productId}/${file.name}`
            );
            await uploadBytes(colorRef, file);
            const url = await getDownloadURL(colorRef);
            return { image: url, color };
          } else if (image && color) {
            return { image, color }; // use existing image
          } else {
            return null; // skip incomplete variant
          }
        })
      );

      const cleanedColors = productColors.filter(Boolean);

      const itemData = {
        productTitle,
        productDescription,
        oldPrice: oldPrice || "",
        productPrice,
        productImage: imgUrl || "",
        inStock: isInStock,
        productColors: cleanedColors,
        brand,
      };

      if (isEdit) {
        const docRef = doc(db, "collection_4", productId);
        await updateDoc(docRef, itemData);
      } else {
        const docRef = doc(db, "collection_4", productId);
        await updateDoc(docRef, itemData);
      }

      // Reset
      setImgFile(null);
      setExistingImgUrl(null);
      setProductTitle("");
      setProductDescription("");
      setOldPrice("");
      setProductPrice("");
      setIsInStock(true);
      setIsEdit(false);
      setEditId(null);
      setColorVariants([{ file: null, color: "" }]);

      const modal = new window.bootstrap.Modal(
        document.getElementById("itemModal")
      );
      modal.hide();
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this product?"
    );
    if (!isConfirmed) return;
    const docRef = doc(db, "collection_4", id);
    await deleteDoc(docRef);
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    setEditId(item.id);
    setProductTitle(item.productTitle);
    setProductDescription(item.productDescription);
    setOldPrice(item.oldPrice);
    setProductPrice(item.productPrice);
    setIsInStock(item.inStock);
    setExistingImgUrl(item.productImage);
    setBrand(item.brand || "fesishop");
    setColorVariants(
      (item.productColors || []).map((colorObj) => ({
        file: null,
        image: colorObj.image,
        color: colorObj.color,
      }))
    );
    const modal = new window.bootstrap.Modal(
      document.getElementById("itemModal")
    );
    modal.show();
  };

  const handleAdd = () => {
    setIsEdit(false);
    setEditId(null);
    setProductTitle("");
    setProductDescription("");
    setOldPrice("");
    setProductPrice("");
    setIsInStock(true);
    setExistingImgUrl(null);
    setBrand("fesishop");
    setColorVariants([{ file: null, color: "" }]);

    const modal = new window.bootstrap.Modal(
      document.getElementById("itemModal")
    );
    modal.show();
  };

  const handleSaveFilter = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to set the filter to "${sortOrder}"?`
    );
    if (!confirmed) return;

    try {
      const filterDocRef = doc(db, "collection_4", "productFilter");
      await setDoc(filterDocRef, { type: sortOrder });
      alert("Filter saved successfully!");
    } catch (error) {
      console.error("Error saving filter:", error);
      alert("Failed to save filter. Check console for details.");
    }
  };

  const handlePriceChange = async (field) => {
    const input = prompt(
      `Enter an amount to change the ${
        field === "oldPrice" ? "Old Price" : "Current Price"
      }:`
    );
    if (!input) return;

    try {
      const querySnapshot = await collection(db, "collection_4");
      onSnapshot(querySnapshot, async (snapshot) => {
        const updates = snapshot.docs.filter((doc) => doc.id !== "heading");

        for (const docSnap of updates) {
          const docRef = doc(db, "collection_4", docSnap.id);
          await updateDoc(docRef, {
            [field]: input,
          });
        }

        alert(`Successfully updated all Product Prices to ${input}`);
      });
    } catch (error) {
      console.error("Error updating prices:", error);
      alert("Failed to update prices.");
    }
  };
  return (
    <div className="page-container">
      {!headingChange ? (
        <h1 className="mt-3">
          {heading ? heading : "Collection 4"}{" "}
          <span
            onClick={handleChangeHeading}
            style={{ cursor: "pointer" }}
            title="Edit Heading"
          >
            &#9998;
          </span>
        </h1>
      ) : (
        <div className="mt-3">
          <input
            className="form-control text-dark"
            placeholder="Enter a new heading"
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            onKeyPress={handleKeyPress}
            autoFocus
          />
        </div>
      )}
      <div className="container mt-5">
        <div className="d-flex align-items-center">
          <button
            className="btn add-data-btn me-2"
            type="button"
            onClick={handleAdd}
          >
            Add Product +
          </button>

          <div className="dropdown">
            <button
              className="btn add-data-btn dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Import Products
            </button>
            <ul className="dropdown-menu">
              {collections.map((collection) => (
                <li
                  className="dropdown-item"
                  role="button"
                  onClick={() =>
                    navigate(
                      `/collection_4/import-products/${collection.route}`
                    )
                  }
                  key={collection.route}
                >
                  {collection.name}
                </li>
              ))}
            </ul>
          </div>
          <div className="dropdown ms-2">
            <button
              className="btn add-data-btn dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              Change Price
            </button>
            <ul className="dropdown-menu">
              <li
                className="dropdown-item"
                role="button"
                onClick={() => handlePriceChange("oldPrice")}
              >
                Old Price
              </li>
              <li
                className="dropdown-item"
                role="button"
                onClick={() => handlePriceChange("productPrice")}
              >
                Current Price
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex align-items-center mb-3">
          <input
            type="text"
            className="form-control me-2"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <select
            className="form-select ms-2"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{ maxWidth: "200px" }}
          >
            <optgroup label="Sort">
              <option value="lowToHigh">Lowest to Highest</option>
              <option value="highToLow">Highest to Lowest</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </optgroup>
          </select>

          <button
            style={{ maxWidth: "100px" }}
            className="btn btn-secondary m-1 ms-2"
            onClick={handleSaveFilter}
          >
            Save Filter
          </button>
        </div>

        <table className="table table-striped table-bordered table-hover rounded table-custom">
          <thead>
            <tr>
              <th>#</th>
              <th>Product Title</th>
              <th>Product Description</th>
              <th>Old Price</th>
              <th>Product Price</th>
              <th>Product Image</th>
              <th>In Stock</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, index) => (
              <tr key={item.id}>
                <td>{index + 1}</td>
                <td>{item.productTitle}</td>
                <td>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: item.productDescription,
                    }}
                  />
                </td>
                <td>{item.oldPrice}</td>
                <td>{item.productPrice}</td>
                <td>
                  <a
                    href={item.productImage}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Image
                  </a>
                </td>
                <td>{item.inStock ? "Yes" : "No"}</td>
                <td>
                  <button
                    className="btn btn-primary btn-sm me-2"
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <div
        className="modal fade"
        id="itemModal"
        tabIndex="-1"
        aria-labelledby="itemModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <form onSubmit={handleSaveItem}>
              <div className="modal-header">
                <h5 className="modal-title" id="itemModalLabel">
                  {isEdit ? "Edit Product" : "Add Product"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                {existingImgUrl && (
                  <div className="form-group">
                    <label>Current Image</label>
                    <img
                      src={existingImgUrl}
                      alt="Product"
                      className="img-fluid m-3 d-block"
                      height={50}
                      width={100}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="imgFile">Upload New Image</label>
                  <input
                    type="file"
                    className="form-control"
                    id="imgFile"
                    accept="image/*"
                    onChange={handleFileChange}
                    required={!isEdit && !existingImgUrl}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="productTitle">Product Title</label>
                  <input
                    type="text"
                    className="form-control"
                    id="productTitle"
                    value={productTitle}
                    onChange={(e) => setProductTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="productDescription">Product Description</label>
                  <TextEditor
                    value={productDescription}
                    onChange={setProductDescription}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="oldPrice">Old Price</label>
                  <input
                    type="text"
                    className="form-control"
                    id="oldPrice"
                    value={oldPrice}
                    onChange={(e) => setOldPrice(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="productPrice">Product Price</label>
                  <input
                    type="text"
                    className="form-control"
                    id="productPrice"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="brand">Brand</label>
                  <select
                    className="form-select"
                    id="brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    required
                  >
                    <option value="fesishop">Fesishop</option>
                    <option value="resin">Resin</option>
                  </select>
                </div>
                <div className="form-group form-check form-switch">
                  <label className="form-check-label" htmlFor="stockSwitch">
                    In Stock
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="stockSwitch"
                    checked={isInStock}
                    onChange={() => setIsInStock(!isInStock)}
                  />
                </div>
                <div className="mt-4">
                  <h5>Color Variants</h5>
                  {colorVariants.map((variant, index) => (
                    <div className="row mb-2" key={index}>
                      {variant.image && (
                        <div className="mt-1">
                          <img src={variant.image} alt="Variant" width={70} />
                        </div>
                      )}
                      <div className="col-md-5">
                        <input
                          type="file"
                          className="form-control"
                          onChange={(e) =>
                            handleColorChange(index, "file", e.target.files[0])
                          }
                        />
                      </div>
                      <div className="col-md-5">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Color Name"
                          value={variant.color}
                          onChange={(e) =>
                            handleColorChange(index, "color", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-2">
                        {index > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => removeColorVariant(index)}
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={addColorVariant}
                  >
                    + Add More Colors
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                >
                  Save changes
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Collection4;
